import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DPCompany } from '../models/DPCompany';
import { DPSitePage } from '../models/enums/DPSitePage';
import { HttpOptions } from '../models/HttpOptions';
import { DPCompliance } from '../models/DPCompliance';
import { DPForm } from '../models/DPForm';
import { DPFormDataResponse } from '../models/DPFormDataResponse';
import { DPFormResult } from '../models/DPFormResult';
import { DPJob } from '../models/DPJob';
import { DPLocation } from '../models/DPLocation';
import { DPUser } from '../models/DPUser';
import { ApiCacheService } from './api-cache.service';
import { ApiUrlService } from './api-url.service';
import { DPUserForm } from '../models/DPUserForm';
import {
  ApiServiceInterface,
  IGetRequestOption,
} from './api.service.interface';
import { DPApplication } from '../models/DPApplication';
import { DPApplicationSettings } from '../models/DPApplicationSettings';
import { DPApplicationDependencies } from '../models/DPApplicationDependencies';
import { DPAddendumResult } from '../models/DPAddendumResult';
import { DPSignatureResponse } from '../models/DPSignatureResponse';
import { DPSignatureData } from '../models/DPSignatureData';

/**
 * API service.
 *
 * This is a base API service which has all API requests.
 *
 * Please refer to ApiUrl service and api documentation at:
 * https://driverportalapi.leetrans.com/swagger/index.html
 */

@Injectable({
  providedIn: 'root',
})
export class ApiService implements ApiServiceInterface {
  constructor(
    private http: HttpClient,
    private cache: ApiCacheService,
    private urls: ApiUrlService
  ) {}

  public getCompanyInfo(companyDomain: string): Observable<DPCompany> {
    return this.get<DPCompany>(this.urls.company(companyDomain));
  }

  public getCustomText(
    companySettingId: number,
    sitePageId: DPSitePage,
    jobId: number | null,
    opts?: IGetRequestOption
  ): Observable<string> {
    return this.post<{ customText: string }>(
      this.urls.companyCustomText(),
      {
        companySettingId,
        sitePageId,
        jobId,
      },
      opts
    ).pipe(map((response) => response.customText));
  }

  public getElectronicForm(userFormID: number, opts?: IGetRequestOption) {
    return this.get<DPFormDataResponse>(
      this.urls.userFormDetails(userFormID),
      null,
      {
        cache: true,
        ...opts,
      }
    );
  }

  public getFormList(jobID: number, opts?: IGetRequestOption) {
    return this.get<DPForm[]>(this.urls.jobForms(jobID), null, {
      cache: true,
      ...opts,
    });
  }

  public setFormId(userId: number, userJobId: string, formId: string) {
    return this.post(this.urls.userForms(), null, {
      userId,
      jobId: userJobId,
      formId,
    });
  }

  public getJobs(companySettingId: number, opts?: IGetRequestOption) {
    return this.get<DPJob[]>(this.urls.allJobs(companySettingId), null, {
      cache: true,
      ...opts,
    });
  }

  public applyToJob(jobID: number) {
    return this.post<{ userJobID: number }>(this.urls.job(jobID));
  }

  public startFormForJob(formID: number, userJobID: number) {
    return this.post<{ userFormID: string }>(
      this.urls.formUserJob(formID, userJobID)
    );
  }

  public startFormForTask(formID: number, taskID: number) {
    return this.post<DPUserForm[]>(this.urls.formTask(formID, taskID));
  }

  public abondonJob(userJobID: number) {
    return this.delete(this.urls.userJob(userJobID));
  }

  public completeJob(UserJobID: number) {
    return this.patch(this.urls.userJob(UserJobID), {
      MarkComplete: true,
    });
  }

  public setJobLocation(UserJobID: number, LocationId: number) {
    return this.patch(this.urls.userJob(UserJobID), {
      LocationId,
    });
  }

  public uploadTaskFiles(taskId: number, files: string[]) {
    return this.post(this.urls.uploadTaskFiles(taskId), files);
  }

  public uploadImages(userJobID: number, imageType: number, files: string[]) {
    return this.post(this.urls.uploadImages(userJobID, imageType), files);
  }

  public saveFormResult(userFormID: number, result: DPFormResult) {
    const questionIDs = Object.keys(result);
    const data = questionIDs.map((qid) => ({
      pageQuestionID: qid,
      value: result[qid],
    }));

    return this.post(this.urls.userForm(userFormID), {
      userAnswers: data,
    });
  }

  public getSignature(opts?: IGetRequestOption) {
    return this.get<DPSignatureResponse>(this.urls.signature());
  }

  public saveSignature(base64Img: string) {
    //const data: DPSignatureData = { signatureBase64: base64Img };

    return this.post(this.urls.signature(), { signatureBase64: base64Img });
  }

  public getFormPDF(userFormID: number, opts?: IGetRequestOption) {
    return this.get<{ pdfForm: string }>(
      this.urls.userFormPDF(userFormID),
      null,
      opts
    );
  }

  public getLocationTree(jobID: number) {
    return this.get<DPLocation>(this.urls.locationTree(jobID));
  }

  public signForm(userFormID: number) {
    return this.post(this.urls.userFormSignature(userFormID));
  }

  public downloadAllForms(userJobID: number, opts?: IGetRequestOption) {
    return this.get<{ pdfForm: string }>(
      this.urls.userJobPDF(userJobID),
      null,
      opts
    );
  }

  public getCompliance(user: DPUser, opts?: IGetRequestOption) {
    return this.get<DPCompliance>(this.urls.compliance(user.DriverID), null, {
      cache: true,
      ...opts,
    });
  }

  public deleteUser(userID: string) {
    return this.delete(this.urls.user(userID));
  }

  public getAvatar(opts?: IGetRequestOption) {
    return this.get<string>(this.urls.avatar(), null, {
      cache: true,
      ...opts,
    });
  }

  public saveAvatar(data: string) {
    return this.post(this.urls.avatar(), `"${data}"`);
  }

  public deleteAvatar() {
    return this.delete(this.urls.avatar());
  }

  public submitFeedback(data: {
    type: string;
    text: string;
    userId?: string;
    error?: string;
  }) {
    return this.post(this.urls.feedback(), data);
  }

  public createUploadTask(imageTypeId: number, driverId: number) {
    return this.post<string>(
      this.urls.uploadTask(imageTypeId, driverId),
      null,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
        responseType: 'text',
      }
    );
  }

  public deleteUserCreatedTask(taskId: number) {
    return this.delete(this.urls.deleteUserCreatedTask(taskId));
  }

  public getCurrentApplication() {
    return this.get<DPApplication>(this.urls.currentApplication());
  }

  public getCurrentApplicationSettings() {
    return this.get<DPApplicationSettings>(
      this.urls.currentApplicationSettings()
    );
  }

  public getCurrentApplicationDependencies() {
    return this.get<DPApplicationDependencies>(
      this.urls.currentApplicationDependencies()
    );
  }

  public createCurrentApplicationAddendum(
    data: DPApplication,
    taskId: number,
    formId: number
  ) {
    return this.post<DPAddendumResult>(
      this.urls.currentApplicationAddendum(taskId, formId),
      data
    );
  }

  public signApplication(userFormID: number, application: DPApplication) {
    return this.post(
      this.urls.currentApplicationSignature(userFormID),
      application
    );
  }

  public get<T>(
    url: string,
    options?: HttpOptions,
    cacheOpts?: IGetRequestOption
  ) {
    if (cacheOpts?.cache && !cacheOpts?.force) {
      return this.cache.getCache(url).pipe(
        switchMap((response: any) => {
          const { data, error } = response;
          if (error) {
            throw error;
          } else if (data) {
            return of(data);
          } else {
            return this.getData<T>(url, options, cacheOpts);
          }
        })
      ) as Observable<T>;
    }

    return this.getData<T>(url, options, cacheOpts) as Observable<T>;
  }

  public post<T, DT = any>(url: string, data?: DT, options?: any) {
    return this.http.post<T>(
      environment.baseURL + url,
      data,
      this.getOptions(options)
    );
  }

  public put<T>(url: string, data: T, options?: any) {
    return this.http.put<T>(
      environment.baseURL + url,
      data,
      this.getOptions(options)
    );
  }

  public patch<T>(url: string, data: any, options?: any) {
    return this.http.patch<T>(
      environment.baseURL + url,
      data,
      this.getOptions(options)
    );
  }

  public delete<T>(url: string, options?: any) {
    return this.http.delete<T>(
      environment.baseURL + url,
      this.getOptions(options)
    );
  }

  public updateCache(url: string, data: any) {
    this.cache.updateCache(url, data);
  }

  private getOptions(custom?: HttpOptions): HttpOptions {
    return {
      ...custom,
      headers: {
        'Content-Type': 'application/json',
        ...custom?.headers,
      },
    };
  }

  private getData<T>(
    url: string,
    options?: HttpOptions,
    cacheOpts?: IGetRequestOption
  ) {
    if (cacheOpts?.cache) {
      this.cache.setLoading(url, true, cacheOpts?.alias);
    }

    return this.http
      .get<T>(environment.baseURL + url, this.getOptions(options))
      .pipe(
        catchError((err) => {
          if (cacheOpts?.cache) {
            this.cache.setCache(url, null, err, cacheOpts?.alias);
          }
          throw err;
        }),
        tap((data: any) => {
          if (cacheOpts?.cache) {
            this.cache.setCache(url, data, null, cacheOpts?.alias);
          }
        })
      );
  }
}
